import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Star } from "lucide-react";
import { listReviewsForAdmin, moderateReview } from "@/lib/reviews.functions";

type Status = "pending" | "approved" | "rejected";

export const Route = createFileRoute("/admin/omdomen")({
  head: () => ({
    meta: [
      { title: "Moderera omdömen | Admin" },
      { name: "description", content: "Granska och publicera kundomdömen." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminReviews,
});

function AdminReviews() {
  const [status, setStatus] = React.useState<Status>("pending");
  const list = useServerFn(listReviewsForAdmin);
  const act = useServerFn(moderateReview);
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["admin-reviews", status],
    queryFn: () => list({ data: { status } }),
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: (vars: { id: string; action: "approve" | "reject" | "delete" }) => act({ data: vars }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-reviews"] });
      toast.success("Uppdaterat");
    },
    onError: (e: any) => toast.error(e?.message || "Misslyckades"),
  });

  return (
    <main className="container-edge py-10">
      <h1 className="text-2xl font-semibold">Kundomdömen</h1>
      <div className="mt-5 flex flex-wrap gap-2">
        {(["pending", "approved", "rejected"] as Status[]).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(s)}
            className={`min-h-[40px] rounded-full border px-4 text-sm ${
              status === s ? "border-accent bg-accent/10 font-semibold" : "border-border"
            }`}
          >
            {s === "pending" ? "Väntar" : s === "approved" ? "Publicerade" : "Nekade"}
          </button>
        ))}
      </div>

      {query.isLoading ? <p className="mt-6 text-sm text-muted-foreground">Laddar…</p> : null}
      {query.isError ? (
        <p className="mt-6 text-sm text-destructive">
          {(query.error as any)?.message || "Behörighet saknas – logga in som admin."}
        </p>
      ) : null}

      <ul className="mt-6 grid gap-4">
        {(query.data?.reviews ?? []).map((r: any) => (
          <li key={r.id} className="rounded-2xl border border-border bg-card p-5">
            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={i}
                    size={14}
                    className={i <= r.rating ? "fill-accent text-accent" : "text-muted-foreground/40"}
                    aria-hidden="true"
                  />
                ))}
              </span>
              <span className="text-sm font-semibold">{r.title || "(ingen rubrik)"}</span>
            </div>
            <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{r.body}</p>
            <p className="mt-3 text-xs text-muted-foreground">
              {r.name} · {r.email} {r.city ? `· ${r.city}` : ""} {r.vehicle ? `· ${r.vehicle}` : ""} ·{" "}
              {new Date(r.created_at).toLocaleString("sv-SE")}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {status !== "approved" ? (
                <button
                  type="button"
                  disabled={mutation.isPending}
                  onClick={() => mutation.mutate({ id: r.id, action: "approve" })}
                  className="min-h-[40px] rounded-full bg-foreground px-4 text-sm font-semibold text-background"
                >
                  Publicera
                </button>
              ) : null}
              {status !== "rejected" ? (
                <button
                  type="button"
                  disabled={mutation.isPending}
                  onClick={() => mutation.mutate({ id: r.id, action: "reject" })}
                  className="min-h-[40px] rounded-full border border-border px-4 text-sm"
                >
                  Neka
                </button>
              ) : null}
              <button
                type="button"
                disabled={mutation.isPending}
                onClick={() => mutation.mutate({ id: r.id, action: "delete" })}
                className="min-h-[40px] rounded-full border border-destructive/40 px-4 text-sm text-destructive"
              >
                Ta bort
              </button>
            </div>
          </li>
        ))}
      </ul>

      {!query.isLoading && !query.isError && (query.data?.reviews ?? []).length === 0 ? (
        <p className="mt-6 text-sm text-muted-foreground">Inga omdömen i denna vy.</p>
      ) : null}
    </main>
  );
}
